import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Bell, X, Check, AlertTriangle, AlertCircle, Sparkles, Copy, Trash2, ExternalLink, RefreshCw } from 'lucide-react';
import { notificacionesAPI, aiAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Tooltip } from './ui';

const NotificationCenter = () => {
    const navigate = useNavigate();
    const { tienePermiso } = useAuth();
    const puedeUsarIa = tienePermiso('usar_ia');
    const [open, setOpen] = useState(false);
    const [notifs, setNotifs] = useState([]);
    const [noLeidas, setNoLeidas] = useState(0);
    const [loading, setLoading] = useState(false);
    const [generandoIa, setGenerandoIa] = useState(false);
    const [mostrarLeidas, setMostrarLeidas] = useState(false);
    const [iaDisponible, setIaDisponible] = useState(true);
    const [iaStatusMsg, setIaStatusMsg] = useState(null);
    const panelRef = useRef(null);

    // Panel popup: todas las no leidas + max 3 leidas (si el switch esta activo).
    // El historial completo vive en /notificaciones.
    const MAX_LEIDAS_PREVIEW = 3;
    const notifsVisibles = mostrarLeidas
        ? [
            ...notifs.filter(n => !n.leida),
            ...notifs.filter(n => n.leida).slice(0, MAX_LEIDAS_PREVIEW)
        ]
        : notifs.filter(n => !n.leida);
    const totalLeidasOcultas = notifs.filter(n => n.leida).length - MAX_LEIDAS_PREVIEW;

    const cargar = async () => {
        setLoading(true);
        try {
            const res = await notificacionesAPI.getAll();
            setNotifs(res.data || []);
            setNoLeidas(res.no_leidas || 0);
        } catch (err) {
            console.error('Error al cargar notificaciones:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        cargar();
        // Refresh cada 5 minutos
        const interval = setInterval(cargar, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, []);

    // Cerrar al hacer click fuera
    useEffect(() => {
        if (!open) return;
        const handleClick = (e) => {
            if (panelRef.current && !panelRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, [open]);

    const marcarLeida = async (id) => {
        try {
            await notificacionesAPI.marcarLeida(id);
            setNotifs(prev => prev.map(n => n.id === id ? { ...n, leida: 1, leida_at: new Date().toISOString() } : n));
            setNoLeidas(prev => Math.max(0, prev - 1));
        } catch (err) {
            toast.error('Error: ' + err.message);
        }
    };

    const marcarTodasLeidas = async () => {
        try {
            await notificacionesAPI.marcarTodasLeidas();
            await cargar();
            toast.success('Todas marcadas como leídas');
        } catch (err) {
            toast.error('Error: ' + err.message);
        }
    };

    // Verificar estado del sistema IA cuando el panel se abre (para deshabilitar
    // el boton "Sugerir con IA" si el circuit breaker esta abierto o el switch
    // global esta apagado).
    useEffect(() => {
        if (!open || !puedeUsarIa) return;
        let alive = true;
        (async () => {
            try {
                const res = await aiAPI.getStatus();
                if (!alive) return;
                const disponible = res.data?.any_available !== false;
                setIaDisponible(disponible);
                if (!disponible) {
                    setIaStatusMsg(!res.data?.ia_activada
                        ? 'Sistema IA desactivado'
                        : 'IA no disponible — revisa ajustes');
                } else {
                    setIaStatusMsg(null);
                }
            } catch {
                if (alive) setIaDisponible(true);  // Fail-open: si falla, permitir intentar
            }
        })();
        return () => { alive = false; };
    }, [open, puedeUsarIa]);

    const generarSugerenciasIa = async () => {
        setGenerandoIa(true);
        try {
            await aiAPI.ejecutarSugerencias();
            toast.success('Generando sugerencias... aparecerán en unos segundos');
            // Refrescar despues de unos segundos
            setTimeout(() => cargar(), 8000);
            setTimeout(() => cargar(), 16000);
        } catch (err) {
            toast.error('Error: ' + err.message);
        } finally {
            setGenerandoIa(false);
        }
    };

    const eliminar = async (id) => {
        try {
            await notificacionesAPI.eliminar(id);
            setNotifs(prev => prev.filter(n => n.id !== id));
        } catch (err) {
            toast.error('Error: ' + err.message);
        }
    };

    const copiarMensaje = (msg) => {
        navigator.clipboard.writeText(msg);
        toast.success('Mensaje copiado');
    };

    const navegar = (notif) => {
        if (notif.accion_url) {
            navigate(notif.accion_url);
            setOpen(false);
            if (!notif.leida) marcarLeida(notif.id);
        }
    };

    const getIcon = (tipo) => {
        if (tipo.startsWith('sugerencia_ia')) return <Sparkles size={16} className="notif-icon-ia" />;
        if (tipo === 'alerta_abandono') return <AlertCircle size={16} className="notif-icon-danger" />;
        return <AlertTriangle size={16} className="notif-icon-warning" />;
    };

    const getTipoLabel = (tipo) => {
        if (tipo.startsWith('sugerencia_ia')) return 'Sugerencia IA';
        if (tipo === 'alerta_abandono') return 'Cotización abandonada';
        if (tipo === 'alerta_sin_comunicacion') return 'Sin comunicación';
        return 'Sistema';
    };

    const formatTime = (dateStr) => {
        if (!dateStr) return '';
        const date = new Date(dateStr);
        const diff = Date.now() - date.getTime();
        const min = Math.floor(diff / 60000);
        const hr = Math.floor(diff / 3600000);
        const day = Math.floor(diff / 86400000);
        if (min < 1) return 'Ahora';
        if (min < 60) return `Hace ${min}m`;
        if (hr < 24) return `Hace ${hr}h`;
        if (day < 7) return `Hace ${day}d`;
        return date.toLocaleDateString('es-PE', { day: '2-digit', month: 'short' });
    };

    return (
        <div className="notif-wrap" ref={panelRef}>
            <button
                className="notif-bell"
                onClick={() => setOpen(!open)}
                aria-label="Notificaciones"
            >
                <Bell size={20} />
                {noLeidas > 0 && (
                    <span className="notif-badge">{noLeidas > 99 ? '99+' : noLeidas}</span>
                )}
            </button>

            {open && (
                <div className="notif-panel">
                    <div className="notif-panel-header">
                        <span className="notif-panel-title">
                            Notificaciones {noLeidas > 0 && <span className="notif-count">({noLeidas})</span>}
                        </span>
                        <div className="notif-actions">
                            {puedeUsarIa && (
                                <Tooltip content={
                                    !iaDisponible
                                        ? (iaStatusMsg || 'IA no disponible')
                                        : (generandoIa ? 'Generando sugerencias…' : 'Sugerir con IA')
                                }>
                                    <button
                                        className="notif-action-btn ai icon-only"
                                        onClick={generarSugerenciasIa}
                                        disabled={generandoIa || !iaDisponible}
                                        aria-label="Sugerir con IA"
                                    >
                                        {generandoIa ? <RefreshCw size={14} className="spin" /> : <Sparkles size={14} />}
                                    </button>
                                </Tooltip>
                            )}
                            <label className={`notif-switch ${mostrarLeidas ? 'on' : ''}`}>
                                <input
                                    type="checkbox"
                                    checked={mostrarLeidas}
                                    onChange={() => setMostrarLeidas(v => !v)}
                                />
                                <span className="notif-switch-track"><span className="notif-switch-knob" /></span>
                                <span className="notif-switch-label">Leídas</span>
                            </label>
                            {noLeidas > 0 && (
                                <Tooltip content="Marcar todas como leídas">
                                    <button className="notif-action-btn" onClick={marcarTodasLeidas}>
                                        <Check size={14} /> Todas
                                    </button>
                                </Tooltip>
                            )}
                            <Tooltip content="Cerrar">
                                <button className="notif-close" onClick={() => setOpen(false)} aria-label="Cerrar">
                                    <X size={18} />
                                </button>
                            </Tooltip>
                        </div>
                    </div>

                    <div className="notif-list">
                        {loading ? (
                            <div className="notif-empty">Cargando...</div>
                        ) : notifsVisibles.length === 0 ? (
                            <div className="notif-empty">
                                <Bell size={32} style={{ opacity: 0.3 }} />
                                <p>{notifs.length === 0 ? 'Sin notificaciones' : 'No hay notificaciones nuevas'}</p>
                                {notifs.length > 0 && !mostrarLeidas && (
                                    <span className="notif-empty-hint">
                                        Activa el switch "Leídas" para ver {notifs.length} leída{notifs.length !== 1 ? 's' : ''}
                                    </span>
                                )}
                            </div>
                        ) : (
                            notifsVisibles.map(n => (
                                <div key={n.id} className={`notif-item ${!n.leida ? 'unread' : 'read'} notif-${n.tipo}`}>
                                    <div className="notif-item-icon">{getIcon(n.tipo)}</div>
                                    <div className="notif-item-body">
                                        <div className="notif-item-header">
                                            <span className="notif-item-tipo">{getTipoLabel(n.tipo)}</span>
                                            <span className="notif-item-time">{formatTime(n.created_at)}</span>
                                        </div>
                                        <div className="notif-item-titulo">{n.titulo}</div>
                                        {n.mensaje && <div className="notif-item-mensaje">{n.mensaje}</div>}
                                        {n.mensaje_sugerido && (
                                            <div className="notif-item-sugerido">
                                                <div className="notif-sugerido-label">Mensaje WhatsApp sugerido:</div>
                                                <div className="notif-sugerido-text">{n.mensaje_sugerido}</div>
                                                <button
                                                    className="notif-sugerido-copiar"
                                                    onClick={() => copiarMensaje(n.mensaje_sugerido)}
                                                >
                                                    <Copy size={12} /> Copiar
                                                </button>
                                            </div>
                                        )}
                                        <div className="notif-item-actions">
                                            {n.accion_url && (
                                                <button className="notif-mini-btn" onClick={() => navegar(n)}>
                                                    <ExternalLink size={12} /> {n.cotizacion_id ? 'Ver cotización' : 'Ver'}
                                                </button>
                                            )}
                                            {!n.leida && (
                                                <button className="notif-mini-btn" onClick={() => marcarLeida(n.id)}>
                                                    <Check size={12} /> Leída
                                                </button>
                                            )}
                                            <button className="notif-mini-btn danger" onClick={() => eliminar(n.id)}>
                                                <Trash2 size={12} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Footer con acceso al historial completo. Siempre visible
                        para que el usuario sepa que existe la pagina dedicada. */}
                    <div className="notif-panel-footer">
                        {mostrarLeidas && totalLeidasOcultas > 0 && (
                            <span className="notif-panel-footer-hint">
                                {totalLeidasOcultas} leída{totalLeidasOcultas !== 1 ? 's' : ''} más en el historial
                            </span>
                        )}
                        <button
                            className="notif-footer-btn"
                            onClick={() => { setOpen(false); navigate('/notificaciones'); }}
                        >
                            Ver historial completo <ExternalLink size={12} />
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default NotificationCenter;
